import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, switchMap, shareReplay, map, tap } from 'rxjs/operators';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

/**
 * @description Компонент для показа примера кода
 * @description Может принимать как файл, для отображения, так и шаблон(template)
 * @description @Input fileName: string - Название файла, который хотите использовать
 * @description @Input template: TemplateRef<void> - TemplateRef, который мы получаем из шаблона по ссылке
 * @description @Input language: string - Язык программирования, нужен для подкраски, по умолчанию typescript
 * @description @Input highlight: boolean - Флаг, нужна ли подсветка синтаксиса, по умолчанию включено
 * @description @Input ignoreWhitespace: boolean - Флаг, игнорируем ли whitespace
 *
 * @example <sb-code language="html" fileName="test.component"></sb-code>
 * @example <sb-code language="html" [template]="template"></sb-code>
 */
@Component({
  selector: 'sb-code',
  templateUrl: './sb-code.component.html',
  styleUrls: ['./sb-code.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  preserveWhitespaces: false,
})
export class SbCodeComponent implements OnChanges, AfterViewInit {
  /** Название файла */
  @Input()
  public fileName: string;

  /** Ссылка на шаблон */
  @Input()
  public template: TemplateRef<void>;

  /** Название языка */
  @Input()
  public language = 'typescript';

  /** Подсветка синтаксиса */
  @Input()
  public highlight = true;

  /** Короткий код */
  @GasInputBoolean()
  @Input()
  @HostBinding('class._short')
  public short: string | boolean = false;

  /** Игнорирование whitespace */
  @Input()
  public ignoreWhitespace = false;

  @Output('emitCode')
  public emitCode$ = new EventEmitter<string>();

  @ViewChild('contentCode', { read: ElementRef })
  public contentCodeEl: ElementRef;

  public code$: Observable<string>;

  public get fileExtension(): string {
    if (!this.fileName) {
      return null;
    }

    const match = this.fileName.match(/\.(\w+)$/);

    return match !== null ? match[1] : null;
  }

  private readonly fileName$ = new ReplaySubject<string>(1);

  constructor(
    private httpClient: HttpClient,
  ) {
    this.code$ = this.fileName$.pipe(
      switchMap(fileName => this.httpClient.get(`/examples/${fileName}`, { responseType: 'text' })),
      map(code => this.removeMetaComments(code, this.language || this.fileExtension)),
      tap((code: string) => this.emitCode$.emit(code)),
      catchError(err => of(err.message)),
      shareReplay(1),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('fileName' in changes) {
      this.fileName$.next(this.fileName);
    }
  }

  public ngAfterViewInit(): void {
    if (!this.contentCodeEl) {
      return;
    }

    this.trimEmptyLines(this.contentCodeEl.nativeElement);
  }

  private removeMetaComments(code: string, language: string): string {
    if (language === 'ts' || language === 'typescript') {
      return code.replace(/(\/\/ @ts-nocheck|\/\/ tslint:disable)\r?\n/gs, '');
    } else {
      return code;
    }
  }

  private trimEmptyLines(node: Element): void {
    function trimmer(childNode: ChildNode, reversed: boolean): boolean {
      if (childNode instanceof Comment) {
        return false;
      }

      if (childNode instanceof Text) {
        childNode.data = reversed
          ? childNode.data.trimRight()
          : childNode.data.trimLeft();

        if (childNode.data.length === 0) {
          childNode.remove();

          return false;
        }
      }

      return true;
    }

    Array.from(node.childNodes).some(childNode => trimmer(childNode, false));
    Array.from(node.childNodes).reverse().some(childNode => trimmer(childNode, true));
  }
}
